import {
	BarChart3,
	Brain,
	CalendarDays,
	type LucideIcon,
	MessageSquare,
	Palette,
	Rss,
	Smartphone,
	Sparkles,
	UsersRound,
	Video,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { CtaAnchor } from "@/src/common/components/cta-link";
import { EyebrowBadge } from "@/src/common/components/eyebrow-badge";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { Reveal } from "@/src/common/components/reveal";
import { SectionHeading } from "@/src/common/components/section-heading";
import { APP_LINKS } from "@/src/common/constants/app-links";
import { env } from "@/src/common/constants/env";
import { MEDIA_LG, useMediaQuery } from "@/src/common/hooks/use-media-query";
import { FeatureBlock } from "@/src/features/components/feature-block";
import { FeatureVisual, type FeatureVisualKind } from "@/src/features/components/feature-visual";

interface FeatureEntry {
	id: string;
	icon: LucideIcon;
	visual: FeatureVisualKind;
	tone?: "ink" | "premium";
}

const FEATURES: ReadonlyArray<FeatureEntry> = [
	{ id: "feed", icon: Rss, visual: "feed" },
	{ id: "messaging", icon: MessageSquare, visual: "messaging" },
	{ id: "events", icon: CalendarDays, visual: "events" },
	{ id: "meetings", icon: Video, visual: "meetings" },
	{ id: "clips", icon: Video, visual: "clips" },
	{ id: "match", icon: UsersRound, visual: "match" },
	{ id: "insights", icon: Sparkles, visual: "insights" },
	{ id: "members", icon: UsersRound, visual: "members" },
	{ id: "analytics", icon: BarChart3, visual: "analytics" },
	{ id: "branded", icon: Smartphone, visual: "branded", tone: "premium" },
	{ id: "theming", icon: Palette, visual: "theming" },
];

export function FeaturesPage() {
	const { t } = useTranslation("features");
	const isLarge = useMediaQuery(MEDIA_LG);

	return (
		<>
			<PageHelmet title={t("meta.title")} description={t("meta.description")} />

			<section className="bg-mesh relative isolate overflow-hidden pt-20 pb-16 md:pt-32 md:pb-20">
				<div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
					<SectionHeading
						as="h1"
						eyebrow={
							<EyebrowBadge tone="ink" icon={<Brain className="h-3 w-3" />}>
								{t("hero.eyebrow")}
							</EyebrowBadge>
						}
						title={t("hero.title")}
						titleAccent={t("hero.title_accent")}
						description={t("hero.subtitle")}
					/>
				</div>
			</section>

			<section className="py-20 md:py-24">
				<div className="mx-auto flex max-w-7xl flex-col gap-24 px-4 sm:px-6 lg:gap-32 lg:px-8">
					{FEATURES.map((entry, index) => (
						<Reveal key={entry.id}>
							<FeatureBlock
								id={entry.id}
								icon={entry.icon}
								tone={entry.tone}
								side={index % 2 === 0 ? "left" : "right"}
								parallax={isLarge}
								visual={<FeatureVisual kind={entry.visual} />}
							/>
						</Reveal>
					))}
				</div>
			</section>

			<section className="py-24 md:py-32">
				<Reveal className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
					<div className="bg-brand-gradient rounded-[2.5rem] px-8 py-16 text-center text-white sm:px-16 sm:py-20">
						<h2 className="text-balance text-3xl font-bold tracking-tight md:text-5xl">{t("closing.title")}</h2>
						<p className="mx-auto mt-4 max-w-xl text-balance text-base text-white/90 md:text-lg">{t("closing.subtitle")}</p>
						<div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
							<CtaAnchor href={APP_LINKS.SIGN_UP} variant="white" size="lg">
								{t("closing.cta_primary")}
							</CtaAnchor>
							<CtaAnchor href={env.CALENDLY_URL} target="_blank" rel="noreferrer" variant="ghost" size="lg">
								{t("closing.cta_secondary")}
							</CtaAnchor>
						</div>
					</div>
				</Reveal>
			</section>
		</>
	);
}
